"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import KundaliFormCard from "@/app/_home/KundaliFormCard";
import KundaliSavedLink from "@/app/_home/KundaliSavedLink";
import DepthPaywall from "@/components/DepthPaywall";
import { reportIntentOffer } from "@/lib/report-intent-offer";
import { getSEOIntentConfigForSource } from "@/lib/seo-intents";
import type { CapturedSEOEntry } from "@/app/_home/KundaliFormCard.parts";
import type { KundaliResponse } from "@/lib/api/kundali";
import type { KundaliLang } from "@/app/_home/KundaliFormCard.i18n";
import CareerSnapshot from "./CareerSnapshot";

/**
 * CareerToolClient — the interactive hero of the dedicated FREE career tool.
 *
 * It REUSES the shared <KundaliFormCard> (no form logic duplicated): the seeker
 * enters birth details → the engine computes the chart → the inline result
 * renders unchanged, and BELOW it we inject (via the additive `renderPostResult`
 * prop) the free 10th-house + career-lord snapshot followed by the
 * <DepthPaywall planId="career_report"> that wraps the existing career
 * PaidReportCTA.
 *
 * Governance:
 *  - tool_first archetype (rule 43): this form is the page hero, above any offer.
 *  - Single primary CTA (rule 39): exactly ONE monetization component
 *    (PaidReportCTA) on the surface; DepthPaywall renders no button of its own.
 *  - Intent → offer (rule 40): the career intent override + reportIntentOffer
 *    ("career-report") sell career_report — the governed primary_offer/never_bury
 *    for the career intent in intent_offer_alignment.yaml. Price/headline come
 *    from that single offer source, never hardcoded here.
 *  - Monetization on the value surface (rule 46): the offer renders at the value
 *    moment (right after the chart), carrying the result.id as reading_id so
 *    checkout opens with the chart already attached — no re-collection.
 *  - Engine computes / LLM frames (rules 24/29): the snapshot reads engine fields
 *    only; the paid depth is described, never revealed (depth-preview.ts).
 */

const SOURCE_PATH = "/career-astrology";
const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

export default function CareerToolClient() {
  // Career-branched SEO intent config → the form sells career_report and shows
  // career-matched hook/chips. Memoized so the override reference is stable
  // across renders (rule 47 — no unstable prop into the form's effects).
  const seoEntryOverride = useMemo<CapturedSEOEntry>(
    () => ({
      config: getSEOIntentConfigForSource(SOURCE_PATH),
      sourcePath: SOURCE_PATH,
    }),
    []
  );

  return (
    <KundaliFormCard
      source="career"
      seoEntryOverride={seoEntryOverride}
      showDailyInsightCard={false}
      renderPostResult={(result, language) => (
        <CareerPostResult result={result} language={language} />
      )}
    />
  );
}

function CareerPostResult({
  result,
  language,
}: {
  result: KundaliResponse;
  language: KundaliLang;
}) {
  const uiLang: "en" | "hi" = language === "hi" ? "hi" : "en";
  // The governed career offer (planId/priceInr/headline/analyticsContext) — the
  // single source of truth, not a hardcoded ₹/headline (rule 40).
  const offer = reportIntentOffer("career-report");

  // Checkout opens directly: the seeker already entered birth details, so we
  // pass the saved reading + the engine birth_details as reportInputs. No
  // collectInputsHref → the inline contact form + Razorpay opens here, on the
  // value surface (rule 46), instead of bouncing back to /free-kundali.
  const reportInputs: Record<string, unknown> = {
    reading_id: result.id,
    name: result.birth_details.name,
    birth_date: result.birth_details.birth_date,
    birth_time: result.birth_details.birth_time ?? undefined,
    birth_place: result.birth_details.birth_place,
    language: uiLang,
  };

  return (
    <div className="mx-auto max-w-md px-4 pt-2 sm:px-6">
      <CareerSnapshot result={result} language={uiLang} />
      <DepthPaywall
        planId="career_report"
        language={uiLang}
        lockedTitle={
          uiLang === "hi"
            ? "आपकी पूरी करियर रिपोर्ट में खुलता है"
            : "Your full career report unlocks"
        }
      >
        <PaidReportCTA
          planId={offer.planId}
          priceInr={offer.priceInr}
          headline={offer.headline}
          subhead={offer.subhead}
          reportInputs={reportInputs}
          readingId={result.id}
          language={uiLang}
          source="career-astrology-tool"
          analyticsContext={{
            ...offer.analyticsContext,
            sourcePath: SOURCE_PATH,
            placement: "career_tool_post_result_offer",
          }}
        />
      </DepthPaywall>
      {/* Durable return path for the inline kundali (rule 46 — the saved link is
          additive, never dropped). `renderPostResult` REPLACES the default
          KundaliPostResultAdjuncts, so the saved link must be re-rendered here
          or the seeker loses their /kundali/<id> return URL. Guarded on
          result.id exactly like the default adjuncts. */}
      {result.id ? <KundaliSavedLink readingId={result.id} language={language} /> : null}
    </div>
  );
}
